import React, { useEffect, useMemo, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useGetDockUtilisationQuery } from "../../api/vmsSlice";
import { analyticsQueryArgs } from "./analyticsFilters";

/**
 * How many hours of today each Bay actually spent occupied, banded as a workload.
 *
 * Deliberately not the live board's "Utilisation" tile, which is the share of Bays holding a vehicle
 * at this instant. Refreshes on the same slow cadence and for the same reason as PeakActivity: one
 * truck arriving fires four camera events, and running the day's aggregation four times to redraw a
 * near-identical set of bars is waste. The bars still advance between polls — see LIVE_TICK_MS.
 */
const UTILISATION_POLL_MS = 300000;
// A loaded Bay is still accruing busy time between polls, so its bar is re-rendered on its own tick.
// A minute, not a second: this is a day's workload, not a stopwatch, and re-rendering every Bay row
// once a second to move a bar by a third of a pixel buys nothing.
const LIVE_TICK_MS = 60000;
const HOUR_MS = 3600000;

// Band presentation only. The boundaries themselves always come from the response, so a plant
// override cannot make a label disagree with the number beside it.
const BANDS = {
	OVER: {
		label: "Over-utilised",
		color: "var(--red-color)",
		describe: (bands) => `${bands.overMinHours}h or more occupied in the day. This workload band does not establish a bottleneck.`,
	},
	OPTIMAL: {
		label: "Optimal",
		color: "var(--green-color)",
		describe: (bands) => `${bands.optimalMinHours}h to ${bands.overMinHours}h occupied in the day. A workload band, not a measure of throughput.`,
	},
	UNDER: {
		label: "Under-utilised",
		color: "var(--primary-color)",
		describe: (bands) => `Under ${bands.optimalMinHours}h occupied in the day. Check live occupancy and reservations before assigning a vehicle.`,
	},
};
const BAND_ORDER = ["OVER", "OPTIMAL", "UNDER"];

// Which workload band an hours figure falls in. The boundaries are always the response's, so a
// plant override cannot make a label disagree with the number beside it.
function bandOf(busyMs, bands) {
	if (busyMs >= bands.overMinHours * HOUR_MS) return "OVER";
	return busyMs >= bands.optimalMinHours * HOUR_MS ? "OPTIMAL" : "UNDER";
}

// Formats a duration the way an operator reads a shift: "9h 10m", or "45m" under the hour.
function formatBusy(milliseconds) {
	const totalMinutes = Math.max(0, Math.round(milliseconds / 60000));
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;
	return hours > 0 ? `${hours}h ${String(minutes).padStart(2, "0")}m` : `${minutes}m`;
}

// Keeps "1 bays" and "1 visits" out of the caption.
function plural(count, word) {
	return `${count} ${word}${count === 1 ? "" : "s"}`;
}

// Day and clock in the plant's timezone, for a carried-over visit whose arrival is not today's date.
function formatClock(value, timezone) {
	if (!value) return "—";
	return new Intl.DateTimeFormat("en-MY", {
		timeZone: timezone, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
	}).format(new Date(value));
}

// Re-renders once a minute, and only while a Bay actually has a vehicle on it.
function useLiveTick(active) {
	const [tick, forceTick] = useState(0);
	useEffect(() => {
		if (!active) return undefined;
		const interval = setInterval(() => forceTick((tick) => tick + 1), LIVE_TICK_MS);
		return () => clearInterval(interval);
	}, [active]);
	return tick;
}

// Where an hours figure sits on the fixed axis. Clamped, so a band boundary configured beyond the
// axis still lands on the track rather than off the end of it.
function axisOffset(hours, axisMs) {
	return `${Math.min(100, Math.max(0, axisMs ? (hours * HOUR_MS / axisMs) * 100 : 0))}%`;
}

/**
 * One Bay's bar. The band boundaries are drawn on the track, so a bar's band is readable without
 * consulting the legend.
 *
 * Time carried in from a vehicle that arrived before midnight is drawn as a lighter leading segment
 * of the same colour. It is real occupancy and it counts, but it is not work the plant did today, and
 * a Bay that opens the day at 13h 58m has to be able to say why.
 */
function UtilisationBar({ row, axisMs, bands, timezone, dayWord }) {
	const band = BANDS[row.band];
	const widthPercent = Math.min(100, axisMs ? (row.busyMs / axisMs) * 100 : 0);
	const carriedPercent = Math.min(widthPercent, axisMs ? (row.carriedOverMs / axisMs) * 100 : 0);
	const label = `${row.dock}, ${formatBusy(row.busyMs)} busy ${dayWord}, ${band.label.toLowerCase()}`
		+ (row.carriedOverMs ? `, of which ${formatBusy(row.carriedOverMs)} carried over from before midnight` : "")
		+ (row.liveSince ? ", vehicle on the bay now" : "");
	return (
		<div className="dock-util-row">
			<span className="dock-util-bay">{row.dock}</span>
			<div className="dock-util-track" role="img" aria-label={label}>
				<span className="dock-util-guide" style={{ left: axisOffset(bands.optimalMinHours, axisMs) }} />
				<span className="dock-util-guide" style={{ left: axisOffset(bands.overMinHours, axisMs) }} />
				<div className="dock-util-fill" style={{ width: `${widthPercent}%`, backgroundColor: band.color }} />
				{carriedPercent > 0 && (
					<div className="dock-util-fill dock-util-carried" style={{ width: `${carriedPercent}%`, backgroundColor: band.color }} />
				)}
				<span className="dock-util-tooltip">
					{row.dock} · {formatBusy(row.busyMs)} busy · {band.label}
					{row.visits ? ` · ${plural(row.visits, "visit")}` : " · no visits"}
					{row.liveSince ? " · loaded now" : ""}
					{row.carriedOverMs > 0 && (
						<>
							<br />
							{formatBusy(row.carriedOverMs)} carried over
							{row.carriedOverFrom ? ` · arrived ${formatClock(row.carriedOverFrom, timezone)}` : ""}
							{row.carriedOverUntil ? ` · left ${formatClock(row.carriedOverUntil, timezone)}` : ""}
						</>
					)}
				</span>
			</div>
			<span className="dock-util-value" style={{ color: band.color }}>
				{formatBusy(row.busyMs)}
				{row.liveSince && <em className="dock-util-live" title="A vehicle is on this bay now, so this figure is still rising"> live</em>}
			</span>
		</div>
	);
}

export default function DockUtilisation({ plantId, canView, filters }) {
	// The day and the Bay all come from the toolbar above, which filters this panel and
	// the time-on-site panel together: read side by side, they must be answering about the same day.
	const { date, selectedBay } = filters;
	const query = useGetDockUtilisationQuery(
		analyticsQueryArgs({ plantId, date, selectedBay }),
		{ skip: !plantId || !canView, pollingInterval: UTILISATION_POLL_MS },
	);
	// `data` can belong to the previous filters while the next request is pending.
	const response = query.currentData;
	const data = response?.data;

	const hasLiveBay = Boolean(data?.rows?.some((row) => row.liveSince));
	const liveTick = useLiveTick(hasLiveBay);

	const bands = data?.bands;
	/**
	 * What to call the window in a sentence. "today" only when it is today: a caption reading "Bay C
	 * worked 9h today" over last Tuesday's figures is not a wording problem, it is a wrong claim.
	 */
	const dayWord = data?.isToday === false ? `on ${data.dateLabel || "that day"}` : "today";
	// The band is computed here rather than server-side because a Bay holding a vehicle is still
	// accruing busy time: a band stamped at generatedAt would be wrong minutes later and would
	// disagree with the dwell timer running on the live board.
	const rows = useMemo(() => {
		if (!data?.rows || !bands) return [];
		const now = Date.now();
		return data.rows.map((row) => {
			const busyMs = row.settledBusyMs + (row.liveSince ? Math.max(0, now - new Date(row.liveSince).getTime()) : 0);
			return { ...row, busyMs, band: bandOf(busyMs, bands) };
		});
		// hasLiveBay drives the minute tick, and the tick is what has to re-run this on a loaded Bay.
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [data, bands, liveTick, query.fulfilledTimeStamp]);

	const counts = BAND_ORDER.reduce((collected, key) => ({
		...collected,
		[key]: rows.filter((row) => row.band === key).length,
	}), {});
	const totalBusyMs = rows.reduce((sum, row) => sum + row.busyMs, 0);
	const totalVisits = rows.reduce((sum, row) => sum + row.visits, 0);
	const unmeasurable = rows.reduce((sum, row) => sum + row.unmeasurableVisits, 0);
	const carriedOver = rows.filter((row) => row.carriedOverFromYesterday);
	const elapsedMs = (Number(data?.elapsedMs) || 0) + (hasLiveBay && data?.to
		? Math.max(0, Date.now() - new Date(data.to).getTime()) : 0);
	const axisMs = (Number(data?.axisHours) || 12) * HOUR_MS;
	const sortedByBusy = [...rows].sort((first, second) => second.busyMs - first.busyMs);
	const busiest = sortedByBusy[0];
	const quietest = sortedByBusy.length > 1 ? sortedByBusy[sortedByBusy.length - 1] : null;
	const selectedBayName = data?.selectedBays?.length === 1 ? data.selectedBays[0].displayName : null;
	const scopeLabel = selectedBayName || "All bays";
	const shareOfDay = (milliseconds) => (elapsedMs ? Math.round((milliseconds / elapsedMs) * 100) : 0);

	// Every figure below is measured from the rows the panel is drawing, so the caption cannot drift
	// from the bars above it.
	let caption = selectedBayName
		? `${selectedBayName} was not occupied ${dayWord}.`
		: `No bay was occupied ${dayWord}.`;
	if (totalBusyMs > 0 && busiest) {
		caption = `${plural(rows.length, "bay")} worked ${formatBusy(totalBusyMs)} ${dayWord} across ${plural(totalVisits, "visit")}`
			+ ` — ${formatBusy(totalBusyMs / rows.length)} average per bay.`
			+ ` ${busiest.dock} is ${BANDS[busiest.band].label.toLowerCase()} at ${formatBusy(busiest.busyMs)}`
			+ ` (${shareOfDay(busiest.busyMs)}% of the ${formatBusy(elapsedMs)} elapsed)`
			+ `${busiest.liveSince ? " and is still loaded" : ""}.`;
		if (quietest && quietest.busyMs < busiest.busyMs) {
			caption += ` ${quietest.dock} has the least recorded occupancy at ${formatBusy(quietest.busyMs)}; current availability is shown on the Live Board.`;
		}
		// Naming the amount is the whole point. "Bay C carries time from before midnight" left an
		// operator staring at 15h 25m on a day of two visits with no way to account for it; "13h 58m
		// of Bay C's 15h 25m" accounts for it in one clause.
		for (const row of carriedOver) {
			if (row.carriedOverMs > 0) {
				caption += ` ${formatBusy(row.carriedOverMs)} of ${row.dock}'s ${formatBusy(row.busyMs)}`
					+ ` carried over from a vehicle that arrived ${formatClock(row.carriedOverFrom, response?.timezone)}`
					+ `${row.carriedOverUntil ? ` and left ${formatClock(row.carriedOverUntil, response?.timezone)}` : ""}.`;
			} else {
				// The carried-over visit is the one still on the Bay, so its whole running total is
				// carried over and there is no settled portion to name.
				caption += ` The vehicle on ${row.dock} arrived ${formatClock(row.carriedOverFrom, response?.timezone)},`
					+ " before midnight, so its whole figure is carried over.";
			}
		}
	}
	// Only worth saying while it is still true: an 8-hour band is unreachable before 08:00 local, and
	// an all-yellow morning is not evidence of an under-used dock.
	if (bands && data?.isToday !== false && elapsedMs < bands.overMinHours * HOUR_MS) {
		caption += ` Bands are absolute hours, so with ${formatBusy(elapsedMs)} of the day elapsed no bay can reach the over-utilised band yet.`;
	}
	if (unmeasurable) {
		caption += ` ${plural(unmeasurable, "visit")} could not be measured — no departure was recorded — so the totals are a floor.`;
	}
	if (data?.capped) {
		caption += " The day was truncated at the scan limit, so these totals are incomplete.";
	}

	if (!canView) return null;

	return (
		<section className="dock-util-panel" aria-label="Dock utilisation">
			<div className="dock-peak-head">
				<div>
					<h2>Dock utilisation — busy hours</h2>
					<span>
						Hours each bay was occupied · {scopeLabel}
						{data?.dateLabel ? ` · ${data.dateLabel}` : ""}
						{response?.timezone ? ` · ${response.timezone.replace("_", " ")}` : ""}
					</span>
				</div>
			</div>

			{(query.error || response?.error) && (
				<div className="dock-message error">{response?.message || "Dock utilisation could not be loaded."}</div>
			)}

			{query.isLoading || (query.isFetching && !response) ? (
				<div className="dock-peak-placeholder">Loading bay workload…</div>
			) : rows.length ? (
				<>
					<div className="dock-util-grid">
						<div className="dock-util-cards">
							{BAND_ORDER.map((key) => (
								<div className="dock-util-card" key={key} style={{ "--dock-band-color": BANDS[key].color }}>
									<div className="dock-util-card-head"><span className="dock-util-dot" />{BANDS[key].label}</div>
									<strong>{counts[key]}<small> {counts[key] === 1 ? "bay" : "bays"}</small></strong>
									<span>{BANDS[key].describe(bands)}</span>
								</div>
							))}
						</div>
						<div className="dock-util-bars">
							{rows.map((row) => (
								<UtilisationBar
									key={row.checkpointNumber}
									row={row}
									axisMs={axisMs}
									bands={bands}
									timezone={response?.timezone}
									dayWord={dayWord}
								/>
							))}
							{/* Shares the row grid, so the boundary labels sit under the guides they name. */}
							<div className="dock-util-axis">
								<span className="dock-util-bay" />
								<div className="dock-util-axis-track">
									<span className="dock-util-axis-end" style={{ left: 0 }}>0h</span>
									<span className="dock-util-axis-mark" style={{ left: axisOffset(bands.optimalMinHours, axisMs), color: BANDS.OPTIMAL.color }}>
										{bands.optimalMinHours}h
									</span>
									<span className="dock-util-axis-mark" style={{ left: axisOffset(bands.overMinHours, axisMs), color: BANDS.OVER.color }}>
										{bands.overMinHours}h
									</span>
									<span className="dock-util-axis-end" style={{ right: 0 }}>{data.axisHours}h</span>
								</div>
								<span className="dock-util-value" />
							</div>
							{/* Only shown when a bar actually carries one, so the legend never explains
							    a segment that is not on screen. */}
							{carriedOver.some((row) => row.carriedOverMs > 0) && (
								<div className="dock-util-legend">
									<span className="dock-util-legend-swatch" />
									Lighter segment · occupied before midnight by a vehicle that arrived yesterday
								</div>
							)}
						</div>
					</div>
					<p className="dock-peak-caption dock-util-caption">
						<InfoOutlinedIcon className="dock-util-caption-icon" aria-hidden="true" />
						<span>{caption}</span>
					</p>
				</>
			) : (
				<div className="dock-peak-placeholder">No bay is configured for this plant.</div>
			)}
		</section>
	);
}
